import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Card from '../../components/ui/Card';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';
import TextArea from '../../components/forms/TextArea';
import { EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';

const roleOptions = [
  { value: 'director', label: 'Centre Director / Owner' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

const provinceOptions = [
  { value: 'EC', label: 'Eastern Cape' },
  { value: 'FS', label: 'Free State' },
  { value: 'GP', label: 'Gauteng' },
  { value: 'KZN', label: 'KwaZulu-Natal' },
  { value: 'LP', label: 'Limpopo' },
  { value: 'MP', label: 'Mpumalanga' },
  { value: 'NC', label: 'Northern Cape' },
  { value: 'NW', label: 'North West' },
  { value: 'WC', label: 'Western Cape' },
];

const ContactPage: React.FC = () => {
  // Demo Request Form
  const [demoForm, setDemoForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    school_name: '',
    role: '',
    num_children: '',
    province: '',
    message: '',
    email_consent: false,
    website_url: '', // honeypot
  });
  const [demoErrors, setDemoErrors] = useState<Record<string, string>>({});
  const [demoSubmitting, setDemoSubmitting] = useState(false);
  const [demoSubmitted, setDemoSubmitted] = useState(false);

  // General Contact Form
  const [contactForm, setContactForm] = useState({
    first_name: '',
    email: '',
    message: '',
    email_consent: false,
    website_url: '', // honeypot
  });
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);

  const validateDemo = () => {
    const errors: Record<string, string> = {};
    if (!demoForm.email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(demoForm.email)) errors.email = 'Invalid email address';
    if (!demoForm.email_consent) errors.email_consent = 'Consent is required';
    return errors;
  };

  const validateContact = () => {
    const errors: Record<string, string> = {};
    if (!contactForm.email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) errors.email = 'Invalid email address';
    if (!contactForm.message) errors.message = 'Message is required';
    if (!contactForm.email_consent) errors.email_consent = 'Consent is required';
    return errors;
  };

  const handleDemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateDemo();
    if (Object.keys(errors).length > 0) {
      setDemoErrors(errors);
      return;
    }
    setDemoErrors({});
    setDemoSubmitting(true);
    try {
      const { leadsApi } = await import('../../services/api/leads');
      await leadsApi.submitContact({
        ...demoForm,
        num_children: demoForm.num_children ? parseInt(demoForm.num_children) : undefined,
        source: 'website',
      });
      setDemoSubmitted(true);
    } catch {
      // Error handled by global handler
    } finally {
      setDemoSubmitting(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateContact();
    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return;
    }
    setContactErrors({});
    setContactSubmitting(true);
    try {
      const { leadsApi } = await import('../../services/api/leads');
      await leadsApi.submitContact({
        first_name: contactForm.first_name,
        email: contactForm.email,
        message: contactForm.message,
        email_consent: contactForm.email_consent,
        website_url: contactForm.website_url,
      });
      setContactSubmitted(true);
    } catch {
      // Error handled by global handler
    } finally {
      setContactSubmitting(false);
    }
  };

  const updateDemoField = (field: string, value: string | boolean) => {
    setDemoForm(prev => ({ ...prev, [field]: value }));
    if (demoErrors[field]) setDemoErrors(prev => ({ ...prev, [field]: '' }));
  };

  const updateContactField = (field: string, value: string | boolean) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
    if (contactErrors[field]) setContactErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <>
      <Helmet>
        <title>Contact — Sprout Educational Platform</title>
        <meta name="description" content="Request a demo, ask questions, or get in touch with the Sprout team. We'd love to hear from South African schools and centres." />
        <meta property="og:title" content="Contact — Sprout Educational Platform" />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/contact" />
      </Helmet>

      {/* Header */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-display font-bold text-gray-900 dark:text-white"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
          >
            Book a demo, ask a question, or just say hello. We'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left: Contact Info + General Form */}
            <div className="space-y-8">
              {/* Contact Info */}
              <Card>
                <h3 className="font-display font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="w-5 h-5 text-primary-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">hello@sprout.education</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPinIcon className="w-5 h-5 text-primary-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">Cape Town, South Africa</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                    The fastest way to reach us is the form on this page or email — we usually reply within one business day.
                  </p>
                </div>
              </Card>

              {/* General Contact Form */}
              <Card>
                <h3 className="font-display font-semibold text-gray-900 dark:text-white mb-4">Send us a message</h3>
                {contactSubmitted ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Message sent! We'll get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    {/* Honeypot */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                      <input
                        type="text"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={contactForm.website_url}
                        onChange={e => updateContactField('website_url', e.target.value)}
                      />
                    </div>

                    <Input
                      label="Name"
                      value={contactForm.first_name}
                      onChange={e => updateContactField('first_name', e.target.value)}
                      placeholder="Your name"
                    />
                    <Input
                      label="Email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={e => updateContactField('email', e.target.value)}
                      error={contactErrors.email}
                      placeholder="you@example.com"
                    />
                    <TextArea
                      label="Message"
                      required
                      value={contactForm.message}
                      onChange={e => updateContactField('message', e.target.value)}
                      error={contactErrors.message}
                      placeholder="How can we help?"
                      rows={3}
                    />
                    <label className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        checked={contactForm.email_consent}
                        onChange={e => updateContactField('email_consent', e.target.checked)}
                        className="mt-1 rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500 dark:bg-gray-800"
                        required
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        I consent to Sprout processing my information in accordance with POPIA.
                        {contactErrors.email_consent && (
                          <span className="text-red-600 block">{contactErrors.email_consent}</span>
                        )}
                      </span>
                    </label>
                    <button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full px-4 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {contactSubmitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </Card>
            </div>

            {/* Right: Demo Request Form */}
            <div className="lg:col-span-2">
              <Card>
                <h2 className="text-xl font-display font-semibold text-gray-900 dark:text-white mb-2">Request a Demo</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                  Tell us about your centre and we'll set up a personalised walkthrough of Sprout.
                </p>

                {demoSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-secondary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white mb-2">Thank you!</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      We've received your demo request. A member of our team will be in touch within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleDemoSubmit} className="space-y-4">
                    {/* Honeypot */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                      <input
                        type="text"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={demoForm.website_url}
                        onChange={e => updateDemoField('website_url', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="First Name"
                        value={demoForm.first_name}
                        onChange={e => updateDemoField('first_name', e.target.value)}
                        placeholder="Jane"
                      />
                      <Input
                        label="Last Name"
                        value={demoForm.last_name}
                        onChange={e => updateDemoField('last_name', e.target.value)}
                        placeholder="Smith"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input
                        label="Email"
                        type="email"
                        required
                        value={demoForm.email}
                        onChange={e => updateDemoField('email', e.target.value)}
                        error={demoErrors.email}
                        placeholder="jane@school.co.za"
                      />
                      <Input
                        label="Phone"
                        type="tel"
                        value={demoForm.phone}
                        onChange={e => updateDemoField('phone', e.target.value)}
                        placeholder="082 123 4567"
                      />
                    </div>

                    <Input
                      label="School / Centre Name"
                      value={demoForm.school_name}
                      onChange={e => updateDemoField('school_name', e.target.value)}
                      placeholder="Sunshine ECD Centre"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Select
                        label="Your Role"
                        value={demoForm.role}
                        onChange={e => updateDemoField('role', e.target.value)}
                        options={roleOptions}
                        placeholder="Select role"
                      />
                      <Input
                        label="Number of Children"
                        type="number"
                        value={demoForm.num_children}
                        onChange={e => updateDemoField('num_children', e.target.value)}
                        placeholder="e.g. 30"
                      />
                      <Select
                        label="Province"
                        value={demoForm.province}
                        onChange={e => updateDemoField('province', e.target.value)}
                        options={provinceOptions}
                        placeholder="Select province"
                      />
                    </div>

                    <TextArea
                      label="Message (optional)"
                      value={demoForm.message}
                      onChange={e => updateDemoField('message', e.target.value)}
                      placeholder="Tell us about your centre and what you're looking for..."
                      rows={4}
                    />

                    <label className="flex items-start space-x-2">
                      <input
                        type="checkbox"
                        checked={demoForm.email_consent}
                        onChange={e => updateDemoField('email_consent', e.target.checked)}
                        className="mt-1 rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500 dark:bg-gray-800"
                        required
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        I agree to be contacted about Sprout and consent to the processing of my personal information in accordance with POPIA.
                        {demoErrors.email_consent && (
                          <span className="text-red-600 block text-xs">{demoErrors.email_consent}</span>
                        )}
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={demoSubmitting}
                      className="w-full sm:w-auto px-8 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {demoSubmitting ? 'Submitting...' : 'Request a Demo'}
                    </button>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
